import { Injectable } from '@angular/core';
import { ListProductReference } from '@epicuro-next/data/lists';
import {
  isCommercialList,
  isEpicShow,
} from '@epicuro-next/features/engagement/list-utils';
import { BasketItemSource } from '@epicuro-next/models/basket';
import {
  activateConfigStrip,
  ConfigStripActivationSource,
} from '@epicuro-next/state/config-strip-state';
import {
  changeProductOrderInListEntity,
  ListEntity,
  loadNextListProducts,
  selectListEntityProductsIsLoadedFactory,
  selectListEntityProductsIsLoadingFactory,
  selectListEntitySearchSortType,
  selectListEntityWithProducts,
} from '@epicuro-next/state/list-entities';
import { selectAvailableProductDeliveryInfo } from '@epicuro-next/state/list-product-reference-entities';
import {
  addProductsManually,
  selectShowProductsAutocompleteEnabled,
} from '@epicuro-next/state/product-list-state';
import {
  selectIsSalesRepOrAdmin,
  selectUser,
} from '@epicuro-next/state/user-state';
import { isDatesEqualInUTC, isNullish } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

interface State {
  isReordering: boolean;
  listEntity?: ListEntity & { products?: Product[] };
  showUnavailableProducts: boolean;
}

@Injectable()
export class ListProductsComponentStore extends ComponentStore<State> {
  private listId$ = new BehaviorSubject<string | null>(null);

  public listEntity$ = this.select((state) => state.listEntity);
  public list$ = this.select(this.listEntity$, (listEntity) => {
    return listEntity?.list;
  });
  public isReordering$ = this.select((state) => state.isReordering);
  public isCommercialList$ = this.select(this.list$, (list) => {
    if (!list) {
      return false;
    }

    return isCommercialList(list);
  });
  public isEpicShow$ = this.select(this.list$, (list) => {
    if (!list) {
      return false;
    }

    return isEpicShow(list);
  });
  public showUnavailableProducts$ = this.select(
    (state) => state.showUnavailableProducts,
  );
  public isSalesRepOrAdmin$ = this.store.pipe(select(selectIsSalesRepOrAdmin));
  public total$ = this.select(
    this.listEntity$,
    this.isCommercialList$,
    (listEntity, isCommercial) => {
      if (!listEntity?.list) {
        return 0;
      }

      if (isCommercial) {
        return listEntity.count;
      }

      return listEntity.list.amount;
    },
  );

  public totalUnavailable$ = this.select(
    this.list$,
    (list) => list?.unavailableProductsCount,
  );
  public totalAvailable$ = this.select(
    this.list$,
    (list) => list?.availableProductsCount,
  );

  public products$ = this.select(
    this.listEntity$,
    (listEntity) => listEntity?.products,
  );

  public allProductsLoaded$ = this.list$.pipe(
    switchMap((list) => {
      if (!list) {
        return of(null);
      }

      return this.store.pipe(
        select(selectListEntityProductsIsLoadedFactory(list.id)),
      );
    }),
  );

  public loading$ = this.list$.pipe(
    switchMap((list) => {
      if (!list) {
        return of(null);
      }

      return this.store.pipe(
        select(selectListEntityProductsIsLoadingFactory(list.id)),
      );
    }),
  );

  public sortType$ = this.listEntity$.pipe(
    switchMap((entity) =>
      this.store.pipe(select(selectListEntitySearchSortType(entity?.listId))),
    ),
  );

  public isOwner$ = this.select(
    this.list$,
    this.store.pipe(select(selectUser)),
    (list, user) => {
      return user?.externalId === list?.creation.user?.externalId;
    },
  );

  public readonly isListDeleted$ = this.select(
    this.list$,
    (list) => !!list?.isDeleted,
  );

  public isDraggable$ = this.select(
    this.isOwner$,
    this.sortType$,
    this.isCommercialList$,
    this.isReordering$,
    this.isListDeleted$,
    (isOwner, sortType, isCommercial, isReordering, isDeleted) => {
      return (
        !isCommercial &&
        isOwner &&
        isNullish(sortType) &&
        !isReordering &&
        !isDeleted
      );
    },
  );

  public totalProducts$ = this.select(
    this.showUnavailableProducts$,
    this.totalAvailable$,
    this.totalUnavailable$,
    (isShowUnavailableProducts, totalAvailable, totalUnavailable) =>
      (isShowUnavailableProducts ? totalUnavailable : totalAvailable) ?? 0,
  );

  private listProductReferenceData$ = this.listId$.pipe(
    switchMap((listId) => {
      if (!listId) {
        return of(null);
      }

      return this.store.pipe(
        select(selectAvailableProductDeliveryInfo(listId)),
      );
    }),
    map((entity) => entity?.data),
    takeUntil(this.destroy$),
  );

  public productReferences$ = this.select(
    this.listProductReferenceData$,
    (data) => {
      return new Map<string, ListProductReference>(
        data?.productReferences.map((reference) => [
          reference.productId,
          reference,
        ]) ?? [],
      );
    },
  );

  public isCommercialListWithMultipleDates$ = this.select(
    this.listProductReferenceData$,
    (data) => {
      if (!data) {
        return false;
      }

      return !isDatesEqualInUTC(
        data.availableItemTotals.fromDeliveryDate,
        data.availableItemTotals.toDeliveryDate,
      );
    },
  );

  public showProductsAutocomplete$ = this.store.pipe(
    select(selectShowProductsAutocompleteEnabled),
  );

  constructor(private store: Store) {
    super({
      isReordering: false,
      showUnavailableProducts: false,
    });
    this.subscribeListEntity();
  }

  public loadProducts = this.effect<void>((void$) => {
    return void$.pipe(
      debounceTime(300),
      concatLatestFrom(() => [this.allProductsLoaded$, this.list$]),
      filter(([, allProductsLoaded, list]) => !allProductsLoaded && !!list),
      tap(([, , list]) => {
        this.store.dispatch(loadNextListProducts({ listId: list.id }));
      }),
    );
  });

  public setList = this.effect<string>((id$) => {
    return id$.pipe(
      distinctUntilChanged(),
      tap((id: string) => {
        this.listId$.next(id);
      }),
    );
  });

  public reorderProductInList(id: string, order: number) {
    this.listEntity$.pipe(take(1)).subscribe((listEntity) => {
      if (!listEntity) {
        return;
      }

      const product = listEntity.products?.find(
        (item) => item.productId === id,
      );

      if (!product) {
        return;
      }

      this.store.dispatch(
        changeProductOrderInListEntity({
          listId: listEntity.listId,
          brandId: product.brandId,
          productId: product.productId,
          order,
        }),
      );
    });
  }

  public addProductsManually() {
    this.listId$.pipe(take(1)).subscribe((listId) => {
      if (!listId) {
        return;
      }

      this.store.dispatch(addProductsManually({ listId }));
    });
  }

  public cardClick(product: Product, index: number) {
    this.listId$.pipe(take(1)).subscribe((listId) => {
      if (!listId) {
        return;
      }

      this.store.dispatch(
        activateConfigStrip({
          product,
          index,
          source: {
            source: ConfigStripActivationSource.List,
            details: { listId: [listId] },
            subSource: BasketItemSource.LIST_LOCAL,
          },
        }),
      );
    });
  }

  private subscribeListEntity() {
    this.listId$
      .pipe(
        distinctUntilChanged(),
        filter(Boolean),
        switchMap((id: string) => {
          return this.store.pipe(select(selectListEntityWithProducts(id)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((listEntity) => {
        this.patchState({
          isReordering: Boolean(listEntity?.reorderingCount),
        });

        if (listEntity?.reorderingCount) {
          return;
        }

        this.patchState({
          listEntity,
          showUnavailableProducts: false,
        });
      });
  }
}
